import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import { CALENDAR_GRID_TIME_FORMAT, TOTAL_HOURS } from './constants';
import { CalendarTimeIndicatorLines } from './types';

dayjs.extend(customParseFormat);
dayjs.extend(timezone);

// minimum minutes an event should occupy for layout/overlap purposes, regardless of its actual duration
export const MIN_EVENT_DURATION_MINUTES = 15;

export const getEffectiveDuration = (duration?: number): number =>
  Math.max(duration ?? MIN_EVENT_DURATION_MINUTES, MIN_EVENT_DURATION_MINUTES);

const formatAndSanitizeTime = (time: Dayjs): string => {
  return time.format(CALENDAR_GRID_TIME_FORMAT).replace(/:00\s/, ' ');
};

export const timeToDayjs = (timeString?: string, inputFormat = 'HH:mm'): dayjs.Dayjs | undefined => {
  if (!timeString) {
    console.warn(`Invalid time format: "${timeString}". Expected format: "${inputFormat}".`);
    // SP-TODO: send sentry error
    return undefined;
  }

  const parsedTime = dayjs(timeString, inputFormat, true); // strict parsing

  if (!parsedTime.isValid()) {
    console.warn(`Invalid time format: "${timeString}". Expected format: "${inputFormat}".`);
    // SP-TODO: send sentry error
    return undefined;
  }

  return parsedTime;
};

export const timeToRowNumber = (timeStr: string): number => {
  const time = timeToDayjs(timeStr); // time should be in "HH:mm" format, e.g. "09:30"

  if (!time) {
    console.error(`Invalid time format: "${timeStr}". Expected format: "HH:mm".`);
    return 1; // default to first row if time is invalid
  }

  return time.hour() * 60 + time.minute() + 1; // +1 because row 1 is header
};

export const timezoneToTimeIndicatorInfo = (tz?: string): [string, number] | undefined => {
  if (!tz) {
    console.warn('No timezone provided for time indicator info.');
    return undefined;
  }

  let time: dayjs.Dayjs;

  try {
    time = dayjs().tz(tz);
  } catch (error) {
    console.error(`Error getting current time for timezone "${tz}":`, error);
    // SP-TODO: send sentry error
    return undefined;
  }

  // return the formatted time and the corresponding row number
  return [formatAndSanitizeTime(time), time.hour() * 60 + time.minute() + 2]; // +2 due to header & seperator row
};

export const getHoverMinutesFromEvent = (
  eventYPosition: number,
  container: HTMLElement,
  pxPerMinute: number
): number | undefined => {
  const headerHeight = container.querySelector('[data-group-header]')?.getBoundingClientRect().height ?? 0;
  const yPosition = Math.max(
    0,
    container.scrollTop + eventYPosition - container.getBoundingClientRect().top - headerHeight - 30 // 30px of spacer row
  );

  const totalMinutes = Math.round(yPosition / pxPerMinute);
  const clampedMinutes = Math.max(0, Math.min(totalMinutes, TOTAL_HOURS * 60));

  return clampedMinutes;
};

export const passesSelectorFilter = (
  element: Element,
  { allow, deny }: { allow: string[]; deny?: string[] }
): boolean => {
  if (!element) return false;

  const allowed = allow.length === 0 || allow.some((selector) => element.matches(selector));

  const denied = deny?.some((selector) => element.closest(selector));

  return allowed && !denied;
};

export const minutesToTimeStr = (totalMinutes: number): string => {
  return formatAndSanitizeTime(dayjs().startOf('day').add(totalMinutes, 'minutes'));
};

export const convertToIdMap = <T extends { id: string }>(items: T[]) =>
  items.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {} as Record<string, T>);

/**
 * Generates an array of hour labels based on the specified time indicator lines.
 *
 * @param timeIndicatorLines - The type of time indicator lines to generate ('hourly', 'half-hourly', or 'none').
 * @returns An array of time labels formatted
 */
export const getHourLabels = (timeIndicatorLines: CalendarTimeIndicatorLines): string[] => {
  const labels = [];

  if (timeIndicatorLines === 'none') return [];

  const intervalMinutes = timeIndicatorLines === 'half-hourly' ? 30 : 60;

  for (let minutes = 0; minutes < TOTAL_HOURS * 60; minutes += intervalMinutes) {
    labels.push(formatAndSanitizeTime(dayjs().startOf('day').add(minutes, 'minutes')));
  }

  return labels;
};
